// json import removed - return plain objects in React Router v7
import type { LoaderFunctionArgs, MetaFunction } from 'react-router';
import { Link, useLoaderData } from 'react-router';
import { motion } from 'framer-motion';
import { BookOpen, Clock, TrendingUp, RefreshCw } from 'lucide-react';
import { useState } from 'react';

import { Trans } from '@lingui/react/macro';

import Header from '~/components/Header';
import { createLogger } from '~/utils/logger.server';
import { prisma, getCurrentUser } from '~/utils/db.server';
import { estimateReadTime, extractFirstImage, extractCategory } from '~/utils/babylovegrowth.server';

// Define loader return type for proper type inference
interface AcademyArticle {
  id: string;
  slug: string;
  title: string;
  metaDescription: string | null;
  languageCode: string;
  publishedAt: string | null;
  viewCount: number;
  createdAt: string;
  readTime: string;
  image: string | null;
  category: string;
}

interface AcademyLoaderData {
  babylovegrowthArticles: AcademyArticle[];
  isAdmin: boolean;
}

export const handle = {
  title: 'Academy - Public Speaking & Communication Tips | Pavone',
  description: 'Learn how to improve your speaking skills, overcome fear of public speaking, and communicate with confidence. Expert guides and practical tips.',
};

const logger = createLogger('AcademyRoute');

const ADMIN_EMAIL = 'jani.nedeljko@gmail.com';

export async function loader(args: LoaderFunctionArgs) {
  await logger.info('Academy route loaded');

  // Check if current user is admin
  const user = await getCurrentUser(args).catch(() => null);
  const isAdmin = user?.email === ADMIN_EMAIL;

  // Fetch published babylovegrowth articles
  const babylovegrowthArticles = await prisma.article.findMany({
    where: { published: true },
    orderBy: { publishedAt: 'desc' },
    select: {
      id: true,
      slug: true,
      title: true,
      metaDescription: true,
      languageCode: true,
      publishedAt: true,
      viewCount: true,
      createdAt: true,
      contentMarkdown: true,
      contentHtml: true,
    },
  });

  // Compute read time, extract image, and determine category on server side
  const articlesWithExtras = babylovegrowthArticles.map((article) => ({
    id: article.id,
    slug: article.slug,
    title: article.title,
    metaDescription: article.metaDescription,
    languageCode: article.languageCode,
    publishedAt: article.publishedAt,
    viewCount: article.viewCount,
    createdAt: article.createdAt,
    readTime: estimateReadTime(article.contentMarkdown || ''),
    image: extractFirstImage(article.contentHtml || ''),
    category: extractCategory(article.title),
  }));

  return Response.json({ babylovegrowthArticles: articlesWithExtras, isAdmin });
}

export const meta: MetaFunction = () => {
  return [
    { title: 'Academy - Public Speaking & Communication Tips | Pavone' },
    { 
      name: 'description', 
      content: 'Learn how to improve your speaking skills, overcome fear of public speaking, and communicate with confidence. Expert guides and practical tips.' 
    },
    { name: 'keywords', content: 'public speaking, communication skills, speaking confidence, presentation skills, overcome fear of public speaking' },
  ];
};

const articles = [
  {
    slug: 'how-to-stop-rambling-in-job-interviews',
    title: 'How to Stop Rambling in Job Interviews and Give Clear Answers',
    excerpt: 'Learn why you ramble under pressure and how to give clear, structured answers. Practical techniques to stop over-explaining and speak with confidence.',
    readTime: '9 min read',
    category: 'Career Success',
    image: '/images/academy/interview-prep-3.jpg',
    date: 'December 22, 2025',
  },
  {
    slug: 'one-skill-makes-interview-answers-sound-professional',
    title: 'The One Skill That Makes Interview Answers Sound Professional',
    excerpt: 'Discover why delivery matters more than perfect wording. Learn how to sound professional, confident, and clear in job interviews with practical speaking techniques.',
    readTime: '9 min read',
    category: 'Career Success',
    image: '/images/academy/interview-prep-2.jpg',
    date: 'December 15, 2025',
  },
  {
    slug: 'how-to-practice-job-interview-answers-out-loud',
    title: 'How to Practice Job Interview Answers Out Loud (So You Don\'t Freeze)',
    excerpt: 'Learn proven techniques to practice interview answers out loud effectively. Stop freezing, reduce rambling, and speak with confidence in your next interview.',
    readTime: '10 min read',
    category: 'Career Success',
    image: '/images/academy/interview-prep.jpg',
    date: 'December 14, 2025',
  },
  {
    slug: 'top-sales-reps-sound-confident',
    title: 'How Do Top Sales Reps Sound So Confident?',
    excerpt: 'Discover the vocal techniques and speaking patterns that make top sales reps sound confident, persuasive, and trustworthy. Learn their exact methods.',
    readTime: '8 min read',
    category: 'Sales & Business',
    image: '/images/academy/salesguy.png',
    date: 'November 20, 2024',
  },
  {
    slug: 'dont-break-chain-method',
    title: 'Don\'t Break The Chain: The Simple Method That Makes You Practice Every Day',
    excerpt: 'Learn Jerry Seinfeld\'s famous productivity method to build unstoppable speaking habits through consistent daily practice.',
    readTime: '7 min read',
    category: 'Productivity & Habits',
    image: '/images/academy/chain.png',
    date: 'November 15, 2025',
  },
  {
    slug: 'articulate-thoughts-clearly',
    title: 'How To Articulate Your Thoughts More Clearly Than 99% Of People',
    excerpt: 'Learn the exact techniques that help top communicators express complex ideas with crystal clarity and precision in any conversation.',
    readTime: '9 min read',
    category: 'Communication Skills',
    image: '/images/academy/articulate.jpg',
    date: 'November 13, 2025',
  },
  {
    slug: 'speak-confidently-meetings-put-on-spot',
    title: 'How to Speak Confidently in Meetings (Even When You\'re Put on the Spot)',
    excerpt: 'Master daily meeting confidence with practical strategies to sound professional and composed, even when unexpectedly asked for your opinion.',
    readTime: '7 min read',
    category: 'Professional Skills',
    image: '/images/academy/zoom.jpg',
    date: 'November 11, 2025',
  },
  {
    slug: 'ai-tools-practice-perfect-presentation-skills',
    title: 'AI Tools That Help You Practice and Perfect Your Presentation Skills',
    excerpt: 'Learn how to sound confident during a presentation with AI coaching tools that give instant feedback on delivery, tone, and clarity.',
    readTime: '8 min read',
    category: 'Presentation Skills',
    image: '/images/academy/presentation_ai.jpg',
    date: 'November 8, 2025',
  },
  {
    slug: 'secret-hooking-viewers-first-3-seconds-reels',
    title: 'The Secret to Hooking Viewers in the First 3 Seconds of Your Reels',
    excerpt: 'Master short video speaking tips that stop the scroll and boost engagement on TikTok, Reels, and YouTube Shorts.',
    readTime: '7 min read',
    category: 'Content Creation',
    image: '/images/academy/reels.jpg',
    date: 'November 7, 2025',
  },
  {
    slug: '5-speaking-habits-boost-sales-performance',
    title: '5 Speaking Habits That Instantly Boost Your Sales Performance',
    excerpt: 'Learn how to sound confident on sales calls and close more deals with these proven speaking habits used by top performers.',
    readTime: '8 min read',
    category: 'Sales & Business',
    image: '/images/academy/sales.jpg',
    date: 'November 6, 2025',
  },
  {
    slug: 'how-to-speak-confidently-in-job-interviews',
    title: 'How to Speak Confidently in Job Interviews',
    excerpt: 'Master interview communication with expert tips on answering questions confidently and impressing hiring managers.',
    readTime: '9 min read',
    category: 'Career Success',
    image: '/images/academy/online_interview.jpg',
    date: 'November 3, 2025',
  },
  {
    slug: 'how-to-speak-confidently-in-front-of-others',
    title: 'How to Speak Confidently in Front of Others',
    excerpt: 'Discover practical strategies to overcome nervousness and speak with confidence in any situation.',
    readTime: '8 min read',
    category: 'Confidence',
    image: '/images/academy/presentation.jpg',
    date: 'November 2, 2025',
  },
];

export default function AcademyIndex() {
  const { babylovegrowthArticles, isAdmin } = useLoaderData<AcademyLoaderData>();
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState<string | null>(null);

  const handleSync = async () => {
    setSyncing(true);
    setSyncResult(null);
    try {
      const response = await fetch('/api/sync-articles', { method: 'POST' });
      const data = await response.json();
      if (data.ok) {
        setSyncResult(`Synced ${data.synced} articles`);
      } else {
        setSyncResult(`Error: ${data.error}`);
      }
    } catch (err) {
      setSyncResult('Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  // Combine static articles with babylovegrowth articles
  const allArticles = [
    ...articles.map((a) => ({ ...a, source: 'static' as const })),
    ...babylovegrowthArticles.map((a) => ({
      slug: a.slug,
      title: a.title,
      excerpt: a.metaDescription || '',
      readTime: a.readTime,
      category: a.category,
      image: a.image || '/images/academy/ai-generated.jpg',
      date: new Date(a.publishedAt || a.createdAt).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      }),
      source: 'babylovegrowth' as const,
      languageCode: a.languageCode,
      viewCount: a.viewCount,
    })),
  ].sort((a, b) => {
    // Sort by date, newest first
    const dateA = new Date(a.date);
    const dateB = new Date(b.date);
    return dateB.getTime() - dateA.getTime();
  });

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Header />

      <main className="flex-1 pt-24 pb-16">
        <div className="max-w-[1080px] mx-auto px-4 sm:px-6 lg:px-8">
          {/* Admin Sync Button */}
          {isAdmin && (
            <div className="mb-6 flex items-center gap-4">
              <button
                onClick={handleSync}
                disabled={syncing}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                <RefreshCw className={`h-4 w-4 ${syncing ? 'animate-spin' : ''}`} />
                {syncing ? 'Syncing...' : 'Sync Articles'}
              </button>
              {syncResult && (
                <span className="text-sm text-gray-600">{syncResult}</span>
              )}
            </div>
          )}

          {/* Hero Section */}
          {/* <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-brand-dark-blue mb-6">
              Pavone Academy
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Master the art of public speaking and effective communication with expert guides,
              practical tips, and proven strategies.
            </p>
          </motion.div> */}

          <h1 className="text-3xl sm:text-4xl font-bold text-brand-dark-blue mb-2">
            <Trans>Pavone Academy</Trans>
          </h1>

          {/* Articles Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mt-8">
            {allArticles.map((article, index) => (
              <motion.div
                key={article.slug}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <Link
                  to={`/academy/${article.slug}`}
                  className="block group h-full"
                >
                  <div className="bg-white rounded-xl border border-gray-200 shadow-md overflow-hidden hover:shadow-xl transition-all duration-300 h-full flex flex-col">
                    {/* Article Image */}
                    <div className="aspect-video bg-gradient-to-br from-brand-dark-blue/10 to-brand-gold/10 overflow-hidden">
                      <img
                        src={article.image}
                        alt={article.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    </div>

                    {/* Article Content */}
                    <div className="p-6 flex-1 flex flex-col">
                      {/* Category & Date */}
                      <div className="flex items-center gap-3 mb-3">
                        <span className="text-xs font-semibold text-brand-gold bg-brand-gold/10 px-3 py-1 rounded-full">
                          {article.category}
                        </span>
                        <span className="text-xs text-gray-500">{article.date}</span>
                      </div>

                      {/* Title */}
                      <h2 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-brand-dark-blue transition-colors">
                        {article.title}
                      </h2>

                      {/* Excerpt */}
                      <p className="text-gray-600 mb-4 flex-1">
                        {article.excerpt}
                      </p>

                      {/* Read Time & Read Button */}
                      <div className="flex items-center justify-between gap-4 mt-auto">
                        <div className="flex items-center gap-2 text-sm text-gray-500">
                          <Clock className="h-4 w-4" />
                          <span>{article.readTime}</span>
                        </div>
                        <span className="inline-flex items-center justify-center px-4 py-2 text-sm font-medium rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 hover:border-brand-dark-blue hover:text-brand-dark-blue transition-colors">
                          <Trans>Read</Trans>
                        </span>
                      </div>
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>

          {/* Coming Soon Section */}
          {/* <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="mt-16 text-center p-12 bg-gradient-to-br from-brand-dark-blue/5 to-brand-gold/5 rounded-2xl"
          >
            <BookOpen className="h-12 w-12 text-brand-gold mx-auto mb-4" />
            <h3 className="text-2xl font-bold text-gray-900 mb-3">
              More Articles Coming Soon
            </h3>
            <p className="text-gray-600 max-w-2xl mx-auto">
              We're continuously adding new guides and resources to help you master public speaking 
              and communication skills. Check back regularly for updates!
            </p>
          </motion.div> */}
        </div>
      </main>

      {/* Footer */}
      <footer className="py-8 px-4 border-t">
        <div className="max-w-[1080px] mx-auto px-4 flex flex-col items-center gap-3 md:flex-row md:justify-between">
          <p className="text-sm text-muted-foreground">
            © 2026 Pavone. <Trans>All rights reserved.</Trans>
          </p>
          <div className="flex items-center gap-6 text-sm">
            <a href="/privacy" className="hover:underline">
              <Trans>Privacy Policy</Trans>
            </a>
            <a href="/terms" className="hover:underline">
              <Trans>Terms of Service</Trans>
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}

